import type { ReactNode } from 'react';
import type { RoleKey } from '@gc-coach/shared';
import styles from './RoleBadge.module.css';

export type RoleBadgeSize = 'sm' | 'md';

export interface RoleBadgeProps {
  /** The closed-enum role key. */
  role: RoleKey;
  /**
   * Visible label. Caller passes the localized string (we keep this
   * component locale-free — labels live in admin/i18n).
   */
  label: ReactNode;
  /** Sizing. Default 'md'. */
  size?: RoleBadgeSize;
  /** Optional extra class. */
  className?: string;
}

/**
 * Swatch dot + role label. Used in:
 *  - Users table Role column
 *  - User detail drawer header
 *  - Invite drawer role radio cards
 *  - Manage Roles permission matrix column headers
 *  - UserMenu role row
 *
 * Swatch colors are spec'd in
 *   design/UI : UX v1.0/for-engineering-users/specs/users-strings.md
 * — hardcoded here per role since they're role-specific design tokens that
 * don't have a higher-level meaning.
 */
export function RoleBadge({ role, label, size = 'md', className }: RoleBadgeProps) {
  const swatch = ROLE_SWATCHES[role];
  return (
    <span
      className={[styles.badge, styles[`size_${size}`], className].filter(Boolean).join(' ')}
    >
      <span className={styles.swatch} style={{ background: swatch }} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
    </span>
  );
}

const ROLE_SWATCHES: Record<RoleKey, string> = {
  super_admin: '#3F2BB8', // brand violet
  content_editor: '#0A7A65', // accent mint
  customer_success: '#2B6CB0', // sky blue
  analyst: '#B86A0F', // warn orange
  read_only: '#565869', // ink3 muted
};
